// @ts-nocheck
/**
 * Refinement item that comes in a collection from the connected control
 */
type RefItem = {
  count: number
  isRefined: boolean
  label: string
  value: string
}

/**
 * Parsed item that will be rendered in a component
 */
type CategoryItem = {
  level: number
  hideCount?: boolean
} & RefItem

const getBreadcrumbs = (label: string): string[] =>
  label.split('/').map(l => l.trim())

const getListWithoutCurrentCategory = (items: RefItem[]): CategoryItem[] =>
  items
    .filter(({ label }) => label.match(/\//g)?.length === 1)
    .map(item => ({
      ...item,
      label: getBreadcrumbs(item.label).pop(),
      level: 1,
    }))

const getCurrentCategoryItem = (currentCategory: RefItem): CategoryItem => {
  const breadcrumbs = getBreadcrumbs(currentCategory.label)

  return {
    ...currentCategory,
    label: breadcrumbs.pop(),
    level: breadcrumbs.length,
    hideCount: true,
  }
}

const getParentCategories = (currentCategory: RefItem): string[] => {
  const parentCategories = []

  if (currentCategory) {
    const breadcrumbs = getBreadcrumbs(currentCategory.label)

    // skip the 1st (current)
    while (breadcrumbs.pop()) {
      parentCategories.unshift(breadcrumbs.join('/'))
    }
  }

  return parentCategories
}

const mapItemsToCategories = (
  items: RefItem[],
  parentCategories: string[]
): CategoryItem[] =>
  items
    .map(item => {
      const { label } = item
      const breadcrumbs = getBreadcrumbs(label)

      if (parentCategories.includes(breadcrumbs.join('/'))) {
        return {
          ...item,
          label: breadcrumbs.pop(),
          level: breadcrumbs.length,
          hideCount: true,
        }
      }

      return null
    })
    .filter(Boolean)

/**
 * Items collection will be mutated, but it is fine since deep cloning
 * would hit the performance during frequent calls of this util.
 */
export const parseCategories = (
  items: RefItem[],
  currentRefinement?: string
): CategoryItem[] => {
  if (!items?.length) {
    return []
  }

  // remove 0 level
  items.shift()

  if (!currentRefinement) {
    return getListWithoutCurrentCategory(items)
  }

  const currentCategory = items.find(item => item.label === currentRefinement)
  const parentCategories = getParentCategories(currentCategory)
  const categories = mapItemsToCategories(items, parentCategories)

  if (currentCategory) {
    const currentCategoryItem = getCurrentCategoryItem(currentCategory)
    categories.push(currentCategoryItem)

    items.forEach(item => {
      const { label } = item
      const breadcrumbs = getBreadcrumbs(label)

      if (
        breadcrumbs.includes(currentCategoryItem.label) &&
        breadcrumbs.length === currentCategoryItem.level + 2
      ) {
        categories.push({
          ...item,
          label: breadcrumbs.pop(),
          level: currentCategoryItem.level + 1,
        })
      }
    })
  }

  return categories
}
